import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  IonContent,
  IonPage,
  IonModal,
  IonSpinner,
  useIonAlert,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import { PageContainer } from "@greeter/matter";
import { Card } from "@greeter/matter-v2";
import {
  IconArrowLeft,
  IconChevronRight,
  IconHanger,
  IconRefresh,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { useMachine, useSelector } from "@xstate/react";
import {
  WardrobeApi,
  WardrobeOverviewMachine,
} from "./WardrobeOverviewMachine";
import {
  createHangerSearch,
  hangerColorLabel,
  mergeHangerSearchResults,
} from "./WardrobeSearch";
import { Routes } from "../../utility/Routes";
import css from "./WardrobeOverviewPage.module.scss";

function dateLabel(value: string | Date | null | undefined) {
  if (value == null) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) || date.getFullYear() <= 1
    ? "—"
    : format(date, "dd.MM.yyyy, HH.mm");
}

export function WardrobeOverviewPage({
  api,
  showBackButton = false,
}: {
  api: WardrobeApi;
  showBackButton?: boolean;
}) {
  const [state, send, actor] = useMachine(WardrobeOverviewMachine, {
    input: { api },
  });
  const router = useIonRouter();
  const [presentAlert] = useIonAlert();

  const [isTablet, setIsTablet] = useState(
    () => window.matchMedia("(min-width: 768px)").matches,
  );
  useEffect(() => {
    const query = window.matchMedia("(min-width: 768px)");
    const update = () => setIsTablet(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useIonViewWillEnter(() => send({ type: "refresh" }), [send]);

  const {
    hangers,
    history,
    focusedId,
    search,
    searchResults,
    focusedHanger: selected,
  } = state.context;

  const listState = useSelector(
    state.context.fetchHangersRef,
    (snapshot) => snapshot.value,
  );

  const loading = listState === "fetching";
  const listFailed = listState === "failed";
  const saving = state.matches({ checkout: "saving" });
  const searchIndex = useMemo(() => createHangerSearch(hangers), [hangers]);
  const visible = mergeHangerSearchResults(
    hangers,
    searchResults,
    search,
    searchIndex,
  );

  const searching =
    state.matches({ search: "debouncing" }) ||
    state.matches({ search: "loading" });

  const searchFailed = state.matches({ search: "failed" });
  const historyLoading = state.matches({ detail: "loading" });

  const detail = selected ? (
    <section className={css.Detail} aria-labelledby="hanger-history-title">
      <header className={css.DetailHeader}>
        <div>
          <h2 id="hanger-history-title">
            Bøjle {selected.hangerId} · Historik
          </h2>
          <small>
            {[selected.hangerArea, hangerColorLabel(selected.hangerColor)]
              .filter(Boolean)
              .join(" · ")}
          </small>
        </div>
        <button
          aria-label="Opdater historik"
          title="Opdater historik"
          aria-busy={historyLoading}
          disabled={saving || historyLoading}
          onClick={() => send({ type: "refreshHistory" })}
        >
          <span className={css.ButtonContent}>
            {historyLoading ? (
              <IonSpinner name="lines-small" />
            ) : (
              <IconRefresh size={22} />
            )}
          </span>
        </button>
        <button
          aria-label="Luk historik"
          disabled={saving}
          onClick={() => send({ type: "focus" })}
        >
          <IconX size={22} />
        </button>
      </header>
      <div className={css.DetailScroll} tabIndex={0} aria-label="Bøjlehistorik">
        {state.matches({ detail: "loading" }) && (
          <div role="status">
            <IonSpinner name="lines-small" /> Henter historik...
          </div>
        )}
        {state.matches({ detail: "failed" }) && (
          <div role="alert">
            Historikken kunne ikke hentes.{" "}
            <button onClick={() => send({ type: "retryHistory" })}>
              Prøv igen
            </button>
          </div>
        )}
        {state.matches({ detail: "ready" }) &&
          (history.length ? (
            <ul className={css.History}>
              {[...history]
                .sort(
                  (a, b) =>
                    (new Date(b.checkedInAt ?? 0).getTime() || 0) -
                    (new Date(a.checkedInAt ?? 0).getTime() || 0),
                )
                .map((item) => (
                  <li
                    key={item.id}
                    className={
                      selected.state === "in-use" && item.checkedOutAt === null
                        ? css.ActiveHistory
                        : undefined
                    }
                  >
                    {selected.state === "in-use" &&
                      item.checkedOutAt === null && (
                        <span className={css.InUse}>I brug</span>
                      )}
                    <strong>
                      {`${item.accountFirstName} ${item.accountLastName}` ||
                        "Ukendt kunde"}
                    </strong>
                    {item.accountEmail?.trim() && (
                      <span>{item.accountEmail.trim()}</span>
                    )}
                    <dl className={css.HistoryDates}>
                      <dt>Ind</dt>
                      <dd>{dateLabel(item.checkedInAt)}</dd>
                      <dt>Ud</dt>
                      <dd>{dateLabel(item.checkedOutAt)}</dd>
                    </dl>
                    {selected.state === "in-use" &&
                      item.checkedOutAt === null && (
                        <div className={css.Actions}>
                          <button
                            className={css.Checkout}
                            disabled={saving || loading}
                            aria-busy={saving}
                            onClick={() =>
                              presentAlert({
                                header: "Tjek bøjlen ud?",
                                subHeader: `Bøjle ${selected.hangerId}`,
                                message:
                                  "Er du sikker på, at du vil tjekke bøjlen ud?",
                                buttons: [
                                  { text: "Annuller", role: "cancel" },
                                  {
                                    text: "Tjek ud",
                                    handler: () => {
                                      if (
                                        actor.getSnapshot().context
                                          .focusedId === selected.id
                                      ) {
                                        send({ type: "checkout" });
                                      }
                                    },
                                  },
                                ],
                              })
                            }
                          >
                            <span className={css.ButtonContent}>
                              {saving && <IonSpinner name="lines-small" />}
                              {saving ? "Tjekker ud..." : "Tjek ud"}
                            </span>
                          </button>
                        </div>
                      )}
                  </li>
                ))}
            </ul>
          ) : (
            <p>Ingen historik for denne bøjle.</p>
          ))}

        {state.matches({ checkout: "failed" }) && (
          <p role="alert">Bøjlen kunne ikke tjekkes ud. Prøv igen.</p>
        )}
      </div>
    </section>
  ) : null;

  return (
    <IonPage>
      <IonContent className={css.Page} scrollY={false}>
        <div className={css.Layout}>
          <main className={css.Main}>
            <header className={css.Header}>
              <div className={css.TitleRow}>
                {showBackButton && (
                  <button
                    type="button"
                    className={css.Back}
                    aria-label="Tilbage til spillested"
                    onClick={() =>
                      router.push(
                        Routes.mainGroup.venueTab.root.route(),
                        "back",
                        "pop",
                      )
                    }
                  >
                    <IconArrowLeft size={18} aria-hidden="true" />
                  </button>
                )}
                <h1>Garderobe</h1>
              </div>
              <div className={css.Summary}>
                <span>
                  {hangers.filter((h) => h.state === "in-use").length} i brug ·{" "}
                  {hangers.length} bøjler
                </span>
                <button
                  className={css.Refresh}
                  disabled={loading || saving}
                  aria-busy={loading}
                  onClick={() => send({ type: "refresh" })}
                >
                  <span className={css.ButtonContent}>
                    {loading ? (
                      <IonSpinner name="lines-small" />
                    ) : (
                      <IconRefresh size={18} />
                    )}
                    {loading ? "Opdaterer..." : "Opdater"}
                  </span>
                </button>
              </div>
              <div className={css.Search}>
                <IconSearch size={20} />
                <input
                  aria-label="Søg efter bøjle"
                  placeholder="Søg efter nummer, område, farve eller navn"
                  value={search}
                  onChange={(e) =>
                    send({ type: "search", query: e.target.value })
                  }
                />
                {search && (
                  <button
                    aria-label="Ryd søgning"
                    onClick={() => send({ type: "search", query: "" })}
                  >
                    ×
                  </button>
                )}
              </div>
            </header>
            <PageContainer className={css.Content}>
              {searching && (
                <div className={css.Message} role="status">
                  <IonSpinner name="lines-small" /> Søger...
                </div>
              )}
              {searchFailed && (
                <div className={css.Message} role="alert">
                  Navnesøgningen kunne ikke gennemføres. Lokale resultater
                  vises.{" "}
                  <button onClick={() => send({ type: "refreshSearch" })}>
                    Prøv igen
                  </button>
                </div>
              )}
              {loading && (
                <div className={css.Message} role="status">
                  <IonSpinner name="lines-small" /> Henter bøjler...
                </div>
              )}
              {listFailed && (
                <div className={css.Message} role="alert">
                  Bøjlerne kunne ikke hentes.
                  <button
                    disabled={saving}
                    onClick={() => send({ type: "refresh" })}
                  >
                    Prøv igen
                  </button>
                </div>
              )}
              {!loading &&
                !listFailed &&
                !searching &&
                !searchFailed &&
                !visible.length && (
                  <p className={css.Message}>
                    {search.trim()
                      ? "Ingen bøjler matcher din søgning."
                      : "Ingen bøjler endnu."}
                  </p>
                )}

              {visible.map((hanger) => {
                const open = focusedId === hanger.id;
                return (
                  <Card key={hanger.id} className={css.HangerCard}>
                    <button
                      className={css.CardHeader}
                      disabled={saving}
                      aria-expanded={open}
                      aria-haspopup={isTablet ? undefined : "dialog"}
                      data-selected={open}
                      data-in-use={hanger.state === "in-use"}
                      onClick={() => send({ type: "focus", id: hanger.id })}
                    >
                      <IconHanger />
                      <span className={css.Identity}>
                        <strong>Bøjle {hanger.hangerId}</strong>
                        <small>
                          {[
                            hanger.hangerArea,
                            hangerColorLabel(hanger.hangerColor),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </small>
                      </span>
                      <span
                        className={
                          hanger.state === "ready" ? css.Ready : css.InUse
                        }
                      >
                        {hanger.state === "ready" ? "Ledig" : "I brug"}
                      </span>
                      <IconChevronRight size={18} />
                    </button>
                  </Card>
                );
              })}
            </PageContainer>
          </main>

          {isTablet && selected && <aside className={css.Pane}>{detail}</aside>}
        </div>
      </IonContent>
      {!isTablet && (
        <IonModal
          className={css.HistoryModal}
          isOpen={!!selected}
          canDismiss={!saving}
          aria-labelledby="hanger-history-title"
          onDidDismiss={() => {
            if (!window.matchMedia("(min-width: 768px)").matches) {
              send({ type: "focus" });
            }
          }}
        >
          {detail}
        </IonModal>
      )}
    </IonPage>
  );
}
